import {
  ClipboardListIcon,
  CloseIcon,
  cn,
  FolderOpenIcon,
  HospitalIcon,
  Paragraph,
  showToast,
  Spinner,
  TriangleExclamationIcon,
  VialCircleCheckIcon,
  VialIcon,
} from '@quickmd-llc/design-system'
import moment from 'moment'
import React, { memo, useState, useCallback, useMemo, useRef } from 'react'

import { useDailyVisit } from 'pages/WaitingRoom/daily/hooks/useDailyVisit'
import { getPatientsPatientIdPrescriptionMonitoringProgram } from 'api/generated/patient/patient'
import { useGetPatientsPatientIdAlerts } from 'api/generated/alert/alert'
import { windowOpenArgs } from 'utils/windowOpenArgs'
import { useSelector } from 'store/hooks/useSelector'
import { selectLiveVisitHdMockEnabled } from 'store/flags/FlagsSelectors'
import { shouldShowPdmp } from 'utils/pdmp'
import { useSingleFlightCallback } from 'hooks/useSingleFlightCallback'
import { addSentryBreadcrumb } from 'utils/sentryUtility'
import { ALLOWED_FILE_EXTENSIONS, ALLOWED_FILE_EXTENSIONS_STR, SIZE_10_MB } from 'constants/fileUpload'
import { errorCodes, getUserFriendlyErrorMessage } from 'utils/errormessages'
import { getDrugScreenComplianceDisplay } from './drugScreenCompliance'
import { getInPersonComplianceDisplay } from './inPersonComplianceDisplay'
import { DrugScreenModal } from './DrugScreenModal'
import { usePatientCardActions } from './usePatientCardActions'

// After opening a resource, briefly ignore repeat clicks. A sticky/bouncing mouse
// button (or a click-and-hold the OS registers as several clicks) would otherwise
// fire these handlers multiple times and open several tabs at once.
const OPEN_COOLDOWN_MS = 500

type Props = {
  // Opens the slide-in Visit Responses panel (online-visit questionnaire / intake-form answers).
  onOpenResponses: () => void
  // Opens the slide-in Alerts panel.
  onOpenAlerts: () => void
}

export const VisitPatientInformation = memo(({ onOpenResponses, onOpenAlerts }: Props) => {
  const { patientWithVisitInfo } = useDailyVisit()
  const {
    cachedPatient,
    isDrugScreenOpen,
    isDrugScreenOpening,
    drugScreenView,
    setDrugScreenView,
    openDrugScreen,
    closeDrugScreen,
  } = usePatientCardActions({
    // @ts-expect-error TS2322 -- MIGRATION: not approved, fix when encountered
    patientId: patientWithVisitInfo.PatientId,
    patient: patientWithVisitInfo,
  })
  const [pdmpFailed, setPdmpFailed] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [pickedFile, setPickedFile] = useState<File | undefined>(undefined)

  const handleStartUpload = useCallback(() => {
    fileInputRef.current?.click()
  }, [])

  const handleFileChosen = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0]
      e.target.value = ''
      if (!file) return

      const ext = (file.name.split('.').pop() ?? '').toLowerCase()
      if (!ALLOWED_FILE_EXTENSIONS.includes(ext)) {
        showToast({
          type: 'error',
          message: getUserFriendlyErrorMessage(errorCodes.INVALID_FILE_FORMAT, '', ALLOWED_FILE_EXTENSIONS_STR),
        })
        return
      }
      if (file.size > SIZE_10_MB) {
        showToast({ type: 'error', message: getUserFriendlyErrorMessage(errorCodes.TOO_BIG_FILE_SIZE, '', '10 MB') })
        return
      }

      setPickedFile(file)
      setDrugScreenView('upload')
    },
    [setDrugScreenView],
  )

  const handleClearFile = useCallback(() => {
    setPickedFile(undefined)
    setDrugScreenView('empty')
    handleStartUpload() // reopen the picker; if cancelled, we're already back on empty
  }, [setDrugScreenView, handleStartUpload])
  const isPatientStateIntegrated = useSelector((state) =>
    state?.flags?.flags?.pdmp_integrated_states?.includes(patientWithVisitInfo.StateCode || ''),
  )

  const { run: onOpenPdmpPage, isRunning: isOpeningPdmp } = useSingleFlightCallback(
    useCallback(async () => {
      try {
        // @ts-expect-error TS2345 -- MIGRATION: not approved, fix when encountered
        const response = await getPatientsPatientIdPrescriptionMonitoringProgram(patientWithVisitInfo.PatientId)

        if (response.ReportUrl) {
          addSentryBreadcrumb({
            category: 'patient-resource-open',
            message: 'Opened PDMP report',
            level: 'info',
            data: { feature: 'pdmp', patientId: patientWithVisitInfo.PatientId },
          })
          window.open(response.ReportUrl, ...windowOpenArgs)
        } else {
          setPdmpFailed(true)
          showToast({ type: 'error', message: 'Failed to load PDMP report.' })
        }
      } catch {
        setPdmpFailed(true)
        showToast({ type: 'error', message: 'Failed to load PDMP report.' })
      }
    }, [patientWithVisitInfo.PatientId]),
    { cooldownMs: OPEN_COOLDOWN_MS },
  )

  // Online/async visits have no intake form; they open the questionnaire responses via a sessionId.
  const isAsyncVisit = patientWithVisitInfo.VisitMethod === 'asynchronous'
  // PRJ-578: with the `live_visit_hd_mock` Dev Tools pin on, live (video) visits open a panel of
  // made-up medication Q&As instead of the intake form. The panel sources them itself (a UI-only
  // mock), so the tile is always openable then. With the pin off, which is the default and the
  // only state where production is served, video visits keep the real intake form.
  const isLiveVisitMockEnabled = useSelector(selectLiveVisitHdMockEnabled)
  const isLiveVisit = isLiveVisitMockEnabled && patientWithVisitInfo.VisitMethod === 'video'
  const questionnaireSession = patientWithVisitInfo.Questionnaires?.[0]
  const questionnaireSessionId = questionnaireSession?.QuestionnaireSessionId
  const canOpenResponse =
    isAsyncVisit ? Boolean(questionnaireSessionId)
    : isLiveVisit ? true
    : Boolean(patientWithVisitInfo.IntakeForm?.CompleteDateTime)
  const responseSubmittedDate =
    isAsyncVisit ? questionnaireSession?.CompleteDateTime : patientWithVisitInfo.IntakeForm?.CompleteDateTime

  // Both intake forms and online-visit questionnaires open in the slide-in Visit Responses panel.
  const handleOpenIntakeForm = useCallback(() => {
    addSentryBreadcrumb({
      category: 'patient-resource-open',
      message: isAsyncVisit ? 'Opened online visit response' : 'Opened intake form',
      level: 'info',
      data: {
        feature: isAsyncVisit ? 'online-visit-response' : 'intake-form',
        patientId: patientWithVisitInfo.PatientId,
      },
    })
    onOpenResponses()
  }, [isAsyncVisit, patientWithVisitInfo.PatientId, onOpenResponses])

  const handleOpenAlerts = useCallback(() => {
    // No patient id: `redactIds` (restclient.ts) strips them from breadcrumb paths because a
    // patient id is a HIPAA Safe-Harbor identifier and breadcrumbs are retained and searchable.
    // The endpoint shape is the whole diagnostic value.
    addSentryBreadcrumb({
      category: 'patient-resource-open',
      message: 'Opened alerts',
      level: 'info',
      data: { feature: 'alerts' },
    })
    onOpenAlerts()
  }, [onOpenAlerts])

  /*
   * `AlertSummary` is the snapshot taken when the visit opened and is never refetched while the
   * popup is up, so the tile drives the alerts query itself rather than waiting for the panel to
   * open. That keeps the count true from the moment the visit opens, lets React Query's
   * focus-refetch update it when an alert is added elsewhere, and means the panel opens on warm
   * cache instead of a spinner — it shares this query key. The snapshot is only a fallback for
   * before the first response lands, or if it fails.
   */
  const { data: fetchedAlerts } = useGetPatientsPatientIdAlerts(patientWithVisitInfo.PatientId || '', {
    query: { enabled: Boolean(patientWithVisitInfo.PatientId) },
  })
  const alertCount = fetchedAlerts?.length ?? patientWithVisitInfo.AlertSummary?.Count ?? 0
  const alertLastUpdated =
    fetchedAlerts ?
      fetchedAlerts.reduce<string | null>(
        (latest, alert) =>
          !latest || new Date(alert.UpdateDateTime ?? 0) > new Date(latest) ? (alert.UpdateDateTime ?? latest) : latest,
        null,
      )
    : patientWithVisitInfo.AlertSummary?.LastUpdateDateTime

  // Read the drug screen from the react-query patient (updated by DrugScreenModal on save) so a
  // provider's own edit refreshes this section live, rather than the stale open-time snapshot.
  const drugScreen = cachedPatient?.DrugScreen ?? patientWithVisitInfo.DrugScreen
  const drugScreenCompliance = useMemo(() => getDrugScreenComplianceDisplay(drugScreen), [drugScreen])
  const drugScreenStatusColor = drugScreenCompliance.cardVariant === 'warning' ? 'text-text-warning' : 'text-text-error'
  const inPersonCompliance = useMemo(
    () => getInPersonComplianceDisplay(patientWithVisitInfo.InPersonComplianceStatus),
    [patientWithVisitInfo.InPersonComplianceStatus],
  )
  const inPersonStatusColor = inPersonCompliance?.cardVariant === 'warning' ? 'text-text-warning' : 'text-text-error'
  return (
    <>
      <div
        className={cn(
          'grid w-full grid-cols-1 gap-y-px border-y border-border-medium bg-border-medium',
          // Online/async visits stay single-column; other visit types split to two columns when wide.
          !isAsyncVisit &&
            'min-[1500px]:!grid-cols-2 min-[1500px]:[&>*:last-child:nth-child(odd)]:col-span-2 min-[1500px]:[&>*:last-child:nth-child(odd)]:border-r-0',
        )}>
        <div
          role='presentation'
          className={cn(
            'pointer-events-none bg-white px-sp-2 py-sp-1',
            !isAsyncVisit && 'min-[1500px]:border-r min-[1500px]:border-border-medium',
            canOpenResponse && 'pointer-events-auto cursor-pointer hover:bg-coastal-blue-10',
          )}
          onClick={handleOpenIntakeForm}>
          <div className='flex w-full !flex-row items-center gap-sp-1'>
            <ClipboardListIcon iconSize='l' />
            <div className='flex flex-1 flex-col'>
              <Paragraph size='md' className='text-text-dark'>
                {isAsyncVisit ?
                  'Visit Responses'
                : isLiveVisit ?
                  'Medication questions'
                : 'Intake form'}
              </Paragraph>
              {isLiveVisit ?
                <Paragraph size='sm' className='text-text-light'>
                  Answered before booking
                </Paragraph>
              : responseSubmittedDate ?
                <div className='flex gap-sp-0.5'>
                  <Paragraph size='sm' className='text-text-light'>
                    Submitted
                  </Paragraph>
                  <Paragraph size='sm'>{moment(responseSubmittedDate).format('MM/DD/YYYY')}</Paragraph>
                </div>
              : <div className='flex items-center'>
                  <CloseIcon iconSize='s' className='text-text-error' />
                  <Paragraph size='sm' className='text-text-error'>
                    None on file
                  </Paragraph>
                </div>
              }
            </div>
          </div>
        </div>
        {/*
          A real button, not a role='presentation' div: this is now the only route to the alerts
          panel, so a div would put the feature out of reach for keyboard and screen-reader users
          entirely. Always clickable, including at zero alerts — the panel owns the empty state.
        */}
        <button
          type='button'
          onClick={handleOpenAlerts}
          className='block cursor-pointer bg-white px-sp-2 py-sp-1 text-left hover:bg-coastal-blue-10 focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-highlight'>
          <div className='flex w-full !flex-row items-center gap-sp-1'>
            <TriangleExclamationIcon iconSize='l' />
            <div className='flex flex-1 flex-col'>
              <Paragraph size='md' className='text-text-dark'>
                Alerts {alertCount ? `(${alertCount})` : ''}
              </Paragraph>
              {alertCount ?
                <div className='flex gap-sp-0.5'>
                  <Paragraph size='sm' className='text-text-light'>
                    Last added
                  </Paragraph>
                  <Paragraph size='sm' className='text-text-dark'>
                    {alertLastUpdated ? moment(alertLastUpdated).format('MM/DD/YYYY') : '--'}
                  </Paragraph>
                </div>
              : <Paragraph size='sm' className='text-text-dark'>
                  None added
                </Paragraph>
              }
            </div>
          </div>
        </button>

        {patientWithVisitInfo.ServiceTypeCategory === 'medicationassistedtreatment' && (
          <div
            role='presentation'
            className={cn(
              'px-sp-2 py-sp-1',
              !isAsyncVisit && 'min-[1500px]:border-r min-[1500px]:border-border-medium',
              'pointer-events-auto cursor-pointer',
              drugScreenCompliance.cardVariant === 'neutral' && 'bg-white hover:bg-coastal-blue-10',
              drugScreenCompliance.cardVariant === 'error' && 'bg-surface-error',
              drugScreenCompliance.cardVariant === 'warning' && 'bg-surface-warning',
              isDrugScreenOpening && 'cursor-wait',
            )}
            onClick={openDrugScreen}>
            <div className='flex w-full !flex-row items-center gap-sp-1'>
              {drugScreenCompliance.mainIcon === 'vialCheck' ?
                <VialCircleCheckIcon iconSize='l' />
              : <VialIcon iconSize='l' />}
              <div className='flex flex-1 flex-col'>
                <Paragraph size='md' className='text-text-dark'>
                  Drug screen
                </Paragraph>

                {drugScreenCompliance.detail.kind === 'date' ?
                  <div className='flex gap-sp-0.5'>
                    <Paragraph size='sm' className='text-text-light'>
                      {drugScreenCompliance.detail.label}
                    </Paragraph>
                    {drugScreen?.CompleteDate ?
                      <Paragraph size='sm' className='text-text-dark'>
                        {moment(drugScreen.CompleteDate).format('MM/DD/YYYY')}
                      </Paragraph>
                    : null}
                  </div>
                : <div className='flex items-center'>
                    {drugScreenCompliance.detail.statusIcon === 'close' ?
                      <CloseIcon iconSize='s' className={drugScreenStatusColor} />
                    : <TriangleExclamationIcon iconSize='s' className={drugScreenStatusColor} />}
                    <Paragraph size='sm' className={drugScreenStatusColor}>
                      {drugScreenCompliance.detail.label}
                    </Paragraph>
                  </div>
                }
              </div>
            </div>
          </div>
        )}
        {shouldShowPdmp(patientWithVisitInfo) && (
          <div
            role='presentation'
            className={cn(
              'pointer-events-none bg-white px-sp-2 py-sp-1',
              isPatientStateIntegrated && !pdmpFailed && 'pointer-events-auto cursor-pointer hover:bg-coastal-blue-10',
              isOpeningPdmp && 'cursor-wait',
            )}
            onClick={onOpenPdmpPage}>
            <div className='flex w-full !flex-row items-center gap-sp-1'>
              <FolderOpenIcon iconSize='l' />
              <div className='flex flex-1 flex-col'>
                <div className='flex items-center gap-sp-1'>
                  <Paragraph size='md' className='text-text-dark'>
                    PDMP
                  </Paragraph>
                  {isOpeningPdmp ?
                    <Spinner iconSize='s' className='text-text-dark' />
                  : null}
                </div>
                {!isOpeningPdmp &&
                  (isPatientStateIntegrated ?
                    pdmpFailed ?
                      <div className='flex items-center'>
                        <CloseIcon iconSize='s' className='text-text-error' />
                        <Paragraph size='sm' className='text-text-error'>
                          Unable to retrieve
                        </Paragraph>
                      </div>
                    : <div className='flex gap-sp-0.5'>
                        <Paragraph size='sm' className='text-text-light'>
                          Viewed
                        </Paragraph>
                        <Paragraph size='sm' className='text-text-dark'>
                          {patientWithVisitInfo.PrescriptionMonitoringProgram?.LastSeenDateTimeUtc ?
                            moment(patientWithVisitInfo.PrescriptionMonitoringProgram?.LastSeenDateTimeUtc).format(
                              'MM/DD/YYYY',
                            )
                          : '--'}
                        </Paragraph>
                      </div>
                  : <Paragraph
                      size='sm'
                      className='text-text-error'>{`${patientWithVisitInfo.StateCode} not integrated`}</Paragraph>)}
              </div>
            </div>
          </div>
        )}
        {inPersonCompliance ?
          <div
            role='presentation'
            className={cn(
              'pointer-events-none px-sp-2 py-sp-1',
              inPersonCompliance.cardVariant === 'neutral' && 'bg-white',
              inPersonCompliance.cardVariant === 'error' && 'bg-surface-error',
              inPersonCompliance.cardVariant === 'warning' && 'bg-surface-warning',
            )}>
            <div className='flex w-full !flex-row items-center gap-sp-1'>
              <HospitalIcon iconSize='l' />
              <div className='flex flex-1 flex-col'>
                <Paragraph size='md' className='text-text-dark'>
                  In-person visit
                </Paragraph>
                {inPersonCompliance.detail.kind === 'text' ?
                  <Paragraph size='sm' className='text-text-light'>
                    {inPersonCompliance.detail.label}
                  </Paragraph>
                : <div className='flex items-center'>
                    <TriangleExclamationIcon iconSize='s' className={inPersonStatusColor} />
                    <Paragraph size='sm' className={inPersonStatusColor}>
                      {inPersonCompliance.detail.label}
                    </Paragraph>
                  </div>
                }
              </div>
            </div>
          </div>
        : null}
      </div>
      {isDrugScreenOpen ?
        // @ts-expect-error TS2375 -- MIGRATION: not approved, fix when encountered
        <DrugScreenModal
          isOpen={isDrugScreenOpen}
          onClose={closeDrugScreen}
          view={drugScreenView}
          // @ts-expect-error TS18048 -- MIGRATION: not approved, fix when encountered
          drugScreen={cachedPatient.DrugScreen}
          patient={cachedPatient}
          onStartUpload={handleStartUpload}
          pickedFile={pickedFile}
          onClearFile={handleClearFile}
          onUploaded={() => {
            setPickedFile(undefined)
            setDrugScreenView('onFile')
          }}
          fileInputRef={fileInputRef}
          onFileChosen={handleFileChosen}
        />
      : null}
    </>
  )
})
